import { CandidateSkillIntersect, CandidateSkillWithScore, SkillLevelId } from 'types'

type SkillLevelObj = {
	Beginner: SkillLevelWithInfo
	Intermediate: SkillLevelWithInfo
	Advanced: SkillLevelWithInfo
	Expert: SkillLevelWithInfo
}

export type SkillLevelWithInfo = {
	levelId: SkillLevelId
	name: string
	description: string
	shortDesc: string
	color: string
}

export type SkillLevelName = keyof SkillLevelObj

export const skillLevelNamesArry: SkillLevelName[] = [
	'Beginner',
	'Intermediate',
	'Advanced',
	'Expert',
]

export const skillLevelsWithInfo: SkillLevelWithInfo[] = skillLevelNamesArry.map((name, idx) => {
	const level = {
		name,
		levelId: (idx + 1) as SkillLevelId,
		description: '',
		shortDesc: '',
		color: '#fff',
	}

	if (idx === 0) {
		level.description = `New to the skill, learning the basics`
		level.shortDesc = 'New to the skill'
		// level.color = '#2BBBB7'
		level.color = '#4D929F'
	} else if (idx === 1) {
		level.description = `Experienced in the skill, learning the intricacies`
		level.shortDesc = 'Showing strength'
		// level.color = '#14AAFF'
		level.color = '#EC9706'
	} else if (idx === 2) {
		level.description = `Leader in this skill, teaching others this skill`
		level.shortDesc = 'Confident and consistent'
		// level.color = '#FF5AF9'
		level.color = '#EE4B22'
	} else if (idx === 3) {
		level.description = `Mastery of this skill, driving innovation in this skill`
		level.shortDesc = 'Top performer'
		// level.color = '#FF4856'
		level.color = '#132F5C'
	}

	return level
})

type SkillRatingWithInfo = {
	min: number // for calculating basic score off of accumulated data
	max: number // for calculating basic score based off of accumulated data
	raw: number // basic score that is submitted during a review
	rating: number // score as displayed to the user
	description: string
}

export const skillRatingWithInfo: SkillRatingWithInfo[] = [
	{
		min: 0, // (0)
		raw: 0.2, // (1.0)
		max: 0.3, // (1.5)
		rating: 1,
		description: 'Learning at this level',
	},
	{
		min: 0.3, // (1.5)
		raw: 0.4, // (2.0)
		max: 0.5, // (2.5)
		rating: 2,
		description: 'Gaining traction at this level',
	},
	{
		min: 0.5, // (2.5)
		raw: 0.6, // (3.0)
		max: 0.7, // (3.5)
		rating: 3,
		description: 'Meeting expectations at this level',
	},
	{
		min: 0.7, // (3.5)
		raw: 0.8, // (4.0)
		max: 0.9, // (4.5)
		rating: 4,
		description: 'Exceeding expectations at this level',
	},
	{
		min: 0.9, // (4.5)
		raw: 1, // (5)
		max: 1, // (5)
		rating: 5,
		description: 'Outstanding at this level',
	},
]

/**
 *  Functions
 * */

export const getSkillLevelFromId = (levelId?: SkillLevelId) =>
	skillLevelsWithInfo.find(l => l.levelId === levelId)

export const getLevelIdFromSkillLevel = (level?: SkillLevelName) =>
	level ? skillLevelNamesArry.findIndex(l => l === level) + 1 : 1

export const calcScoreForDisplay = (score?: number | null) =>
	score && score >= 0.1 ? Math.round((score * 5 * 10) / 10) : 0

export const isVettedSkill = (skill?: CandidateSkillWithScore) => skill?.overallVetting === 1

export const getRatingInfoFromScore = (score = 0) =>
	skillRatingWithInfo.find(s => (score === 1 && s.max === 1) || score <= s.max)

/**
 *  PARSE SKILLS BY LEVEL
 * */

type SkillsByLevel = {
	id: number
	name: string
	unVettedSkills: CandidateSkillIntersect[]
	vettedSkills: CandidateSkillIntersect[]
}

const sortSkills = (a: CandidateSkillIntersect, b: CandidateSkillIntersect): number => {
	const aWithScore = a as CandidateSkillWithScore
	const bWithScore = b as CandidateSkillWithScore
	if (aWithScore.score > bWithScore.score) return -1
	else return +1
}

export type ParseSkillsByLevelOptions = {
	view?: 'public' | 'private' | 'preview'
}
export const parseSkillsByLevel = (
	skills?: CandidateSkillIntersect[],
	options?: ParseSkillsByLevelOptions
) => {
	const { view } = options || {}

	const parsedSkills = skills
		?.reduce((arry, skill) => {
			const { currentSkillLevelId, visible, ...skillWithScore } = skill
			const { overallVetting } = skillWithScore as CandidateSkillWithScore

			if (view === 'public' && (!visible || overallVetting > 1)) return arry

			const { levelId, name } =
				skillLevelsWithInfo.find(l => l.levelId === currentSkillLevelId) ||
				({} as SkillLevelWithInfo)
			const levelIdx = arry.findIndex(s => s.id === currentSkillLevelId)

			if (levelIdx >= 0) {
				if (overallVetting === 1) arry[levelIdx].vettedSkills.push(skill)
				else arry[levelIdx].unVettedSkills.push(skill)
			} else {
				const level: SkillsByLevel = {
					id: levelId,
					name,
					unVettedSkills: [],
					vettedSkills: [],
				}

				if (overallVetting === 1) level.vettedSkills.push(skill)
				else level.unVettedSkills.push(skill)

				arry.push(level)
			}

			return arry
		}, [] as SkillsByLevel[])
		.sort((a, b) => {
			// sort by level - expert at the top beginner at the bottom
			if (a.id > b.id) return -1
			else return +1
		})
		.map(l => ({
			...l,
			vettedSkills: l.vettedSkills.sort(sortSkills),
			unVettedSkills: l.unVettedSkills.sort(sortSkills),
		}))

	return parsedSkills
}
